import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { formatAmount } from '../../datasets/utils/formatters';
import { Database, Loader2, Info } from 'lucide-react';

interface VolumeData {
  payments: {
    monthlyVolume: number | '';
    averageAmount: number | '';
  };
  payouts: {
    monthlyVolume: number | '';
    averageAmount: number | '';
  };
}

interface VolumeStepProps {
  volume: VolumeData;
  onChange: (volume: VolumeData) => void;
}

interface Dataset {
  id: string;
  name: string;
  total_transactions: number;
  total_volume: number;
}

interface DatasetEstimate {
  daysOfData: number;
  rawPaymentCount: number;
  rawPayoutCount: number;
  scaleFactor: number;
}

export function VolumeStep({ volume, onChange }: VolumeStepProps) {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<string>('');
  const [estimate, setEstimate] = useState<DatasetEstimate | null>(null);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    try {
      const { data, error } = await supabase
        .from('datasets')
        .select('id, name, total_transactions, total_volume')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDatasets(data || []);
    } catch (err) {
      console.error('Error loading datasets:', err);
    }
  };

  const updatePayments = (field: keyof typeof volume.payments, value: string) => {
    const numericValue = value === '' ? '' : parseFloat(value);
    setEstimate(null);
    onChange({
      ...volume,
      payments: {
        ...volume.payments,
        [field]: numericValue
      }
    });
  };

  const updatePayouts = (field: keyof typeof volume.payouts, value: string) => {
    const numericValue = value === '' ? '' : parseFloat(value);
    setEstimate(null);
    onChange({
      ...volume,
      payouts: {
        ...volume.payouts,
        [field]: numericValue
      }
    });
  };

  const handleDatasetSelect = async (datasetId: string) => {
    setSelectedDataset(datasetId);
    setEstimate(null);
    if (!datasetId) return;

    const dataset = datasets.find(d => d.id === datasetId);
    if (!dataset) return;

    try {
      const { data: stats, error: statsError } = await supabase
        .rpc('get_dataset_transaction_stats', {
          dataset_id_param: datasetId
        });

      if (statsError) {
        console.error('Error getting transaction stats:', statsError);
        return;
      }

      const daysSpan = stats?.date_range?.days_span || 1;
      const scaleFactor = 30 / daysSpan;

      const rawPaymentCount = stats?.payment?.count || 0;
      const rawPayoutCount = stats?.payout?.count || 0;

      setEstimate({
        daysOfData: Math.round(daysSpan * 10) / 10,
        rawPaymentCount,
        rawPayoutCount,
        scaleFactor: Math.round(scaleFactor * 100) / 100,
      });

      onChange({
        payments: {
          monthlyVolume: Math.round(rawPaymentCount * scaleFactor),
          averageAmount: stats?.payment?.avg_amount ? Number(stats.payment.avg_amount.toFixed(2)) : 0
        },
        payouts: {
          monthlyVolume: Math.round(rawPayoutCount * scaleFactor),
          averageAmount: stats?.payout?.avg_amount ? Number(stats.payout.avg_amount.toFixed(2)) : 0
        }
      });
    } catch (err) {
      console.error('Error getting transaction stats:', err);
    }
  };

  return (
    <div className="space-y-8">
      {/* Dataset Import */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Database className="w-5 h-5 mr-2 text-indigo-600" />
          Import from Dataset
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Select a dataset to automatically fill in the volume information based on historical data.
        </p>
        <select
          value={selectedDataset}
          onChange={(e) => handleDatasetSelect(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="">Select a dataset...</option>
          {datasets.map((dataset) => (
            <option key={dataset.id} value={dataset.id}>
              {dataset.name} ({dataset.total_transactions.toLocaleString()} transactions)
            </option>
          ))}
        </select>

        {estimate && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">
                Estimated monthly volume from {estimate.daysOfData} day{estimate.daysOfData !== 1 ? 's' : ''} of data
              </p>
              <p className="mt-1 text-blue-600">
                Actual: {estimate.rawPaymentCount.toLocaleString()} payments, {estimate.rawPayoutCount.toLocaleString()} payouts
                {estimate.scaleFactor !== 1 && (
                  <span> &times; {estimate.scaleFactor.toFixed(1)} = ~30 day estimate</span>
                )}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Payment Volume */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Volume (eTransfer Request Money)</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {estimate ? 'Est. Monthly Transactions' : 'Monthly Transactions'}
            </label>
            <input
              type="number"
              min="0"
              value={volume.payments.monthlyVolume === '' ? '' : volume.payments.monthlyVolume}
              onChange={(e) => updatePayments('monthlyVolume', e.target.value)}
              className="mt-1 block w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Average Amount
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={volume.payments.averageAmount === '' ? '' : volume.payments.averageAmount}
                onChange={(e) => updatePayments('averageAmount', e.target.value)}
                className="block w-full"
                required
              />
            </div>
          </div>
        </div>

        {volume.payments.monthlyVolume > 0 && volume.payments.averageAmount > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              {estimate ? 'Est. ' : ''}Monthly Payment Volume: {formatAmount(
                (volume.payments.monthlyVolume || 0) * (volume.payments.averageAmount || 0)
              )}
            </p>
          </div>
        )}
      </div>

      {/* Payout Volume */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payout Volume (eTransfer Send Money)</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {estimate ? 'Est. Monthly Transactions' : 'Monthly Transactions'}
            </label>
            <input
              type="number"
              min="0"
              value={volume.payouts.monthlyVolume === '' ? '' : volume.payouts.monthlyVolume}
              onChange={(e) => updatePayouts('monthlyVolume', e.target.value)}
              className="mt-1 block w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Average Amount
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={volume.payouts.averageAmount === '' ? '' : volume.payouts.averageAmount}
                onChange={(e) => updatePayouts('averageAmount', e.target.value)}
                className="block w-full"
                required
              />
            </div>
          </div>
        </div>

        {volume.payouts.monthlyVolume > 0 && volume.payouts.averageAmount > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              {estimate ? 'Est. ' : ''}Monthly Payout Volume: {formatAmount(
                (volume.payouts.monthlyVolume || 0) * (volume.payouts.averageAmount || 0)
              )}
            </p>
          </div>
        )}
      </div>

      {/* Total Volume Summary */}
      {(volume.payments.monthlyVolume > 0 || volume.payouts.monthlyVolume > 0) && (
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-indigo-900 mb-2">
            {estimate ? 'Est. Total Monthly Volume' : 'Total Monthly Volume'}
          </h3>
          <p className="text-2xl font-bold text-indigo-600">
            {formatAmount(
              ((volume.payments.monthlyVolume || 0) * (volume.payments.averageAmount || 0)) +
              ((volume.payouts.monthlyVolume || 0) * (volume.payouts.averageAmount || 0))
            )}
          </p>
        </div>
      )}
    </div>
  );
}